import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import React, { useState } from "react";
import {
  Box,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  type IconName,
  type Omittable,
} from "@salestrak/ui";

import type { IssueRow } from "#app/lib/data";
import { EntityTag } from "#app/components/entity";
import {
  StageSelector,
  IssueTypeSelector,
} from "#app/components/entity-selectors";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { DateText } from "#app/components/date-text";
import {
  CardTokenShell,
  type FieldLabelMode,
  type FieldLayout,
  type TextLabelLayout,
} from "./issue-card-fields";

// ---------------------------------------------------------------------------
// Extra card body properties — Stage, Workflow, Project Type, Created,
// Updated. All default OFF (display options).
//
// Every valued body property is a card TOKEN: the same two-segment shell the
// custom fields and the address row render, so the card's rows share ONE
// geometry top to bottom — a glyph rail (the property glyph in icon mode,
// its NAME in text mode; 24px bare, 28px bordered) and a value segment whose
// 8px inset is where every value starts, chip or text or entity tag.
// Properties keep the plain label chrome (no muted band): they read as the
// card's own rows, not user data.
//
// Stage and Project Type are EDITABLE: the token washes on hover (the card
// grammar's "wash = editable") and pressing opens a selector popover (the
// list cells' pickers — never a dialog). Workflow, Created and Updated are
// read-only faces: no wash, and their press falls through to the card (open
// issue).
// ---------------------------------------------------------------------------

/** Which extra properties exist, in default card order (after address). */
export type ExtraPropertyKey =
  | "stage"
  | "workflow"
  | "projectType"
  | "created"
  | "updated";

export const EXTRA_PROPERTY_ORDER: ExtraPropertyKey[] = [
  "stage",
  "workflow",
  "projectType",
  "created",
  "updated",
];

/** Does the issue carry a value for this extra property? Empty ⇒ the token
 *  doesn't render (parity with address, which only shows when valued —
 *  never "—"). */
export function hasExtraPropertyValue(
  key: ExtraPropertyKey,
  issue: IssueRow,
): boolean {
  switch (key) {
    case "stage":
      return !!issue.stageId;
    case "workflow":
      return !!issue.workflowId;
    case "projectType":
      return !!issue.issueType;
    case "created":
      return !!issue.creationDate;
    case "updated":
      return !!issue.lastUpdateDate;
  }
}

/** Wrap the token in a selector popover. Content mounts only while open — a
 *  board of cards carries zero idle selectors.
 *
 *  It pins NO width: `PopoverPopup` sizes to its content, and the inline
 *  selector inside already asks for the one selector width. A number here is a
 *  second opinion about how wide a picker is — and the narrower one wins, which
 *  is what clipped these popups. */
export function SelectorPopover({
  chip,
  content,
  sheetTitle,
  nativeButton = false,
}: {
  /** The trigger — the property token (owns its press guard). */
  chip: React.ReactElement;
  content: (close: () => void) => React.ReactNode;
  /** The phone sheet's caption — the token's property name. */
  sheetTitle?: React.ReactNode;
  /** True when `chip` is a real `<button>`. A token is a `div`, and Base UI
   *  gives a non-native trigger the button role that its aria-expanded
   *  needs. */
  nativeButton?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    // `responsive`: a bottom sheet on a phone — the inline selector's search
    // takes the keyboard, and a card may sit inside a sheet (the map's).
    <Popover open={open} onOpenChange={setOpen} responsive>
      <PopoverTrigger nativeButton={nativeButton} render={chip} />
      <PopoverPopup align="start" padding="none" sheetTitle={sheetTitle}>
        {open ? content(() => setOpen(false)) : null}
      </PopoverPopup>
    </Popover>
  );
}

export type IssueCardPropertyTokenProps = Omittable<{
  /** Property NAME — the text-mode label segment, and the edit aria-label. */
  label: string;
  /** Property glyph — the icon-mode label segment. */
  icon: IconName;
  /** Viewer may edit: the token washes and owns its press (the wrapping
   *  selector trigger opens the picker). Read-only has no picker, so the
   *  press falls through to the card. */
  canApply: boolean;
  /** `"value"` for properties whose value leads with its own identity visual
   *  (stage, workflow, type) — see `CardTokenShell.labelGlyph`. */
  labelGlyph?: "type" | "value";
  labelMode: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout: FieldLayout;
  labelRail?: number;
  ref?: React.Ref<HTMLDivElement>;
  /** Base UI's injected trigger press — a `render` child receives it as a
   *  plain prop, and the token chains it behind its own propagation guard. */
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}>;

/** The one outer geometry for valued card properties — {@link CardTokenShell}
 *  with the property's glyph or name in the label segment.
 *
 *  The rest-spread is LOAD-BEARING: this sits as a `render` trigger child, so
 *  Base UI's injected trigger props (onClick, ref, aria state) must land on
 *  the shell or the press never opens the picker. The injected `onClick` is
 *  the picker's open — it is CHAINED behind the propagation guard below, never
 *  replaced by it: a bare `onClick={stopPropagation}` on the shell would
 *  shadow the trigger's handler and leave the token focusable but inert. */
export function IssueCardPropertyToken({
  label,
  icon,
  canApply,
  labelGlyph,
  labelMode,
  labelLayout,
  layout,
  labelRail,
  children,
  onClick,
  ...rest
}: IssueCardPropertyTokenProps) {
  return (
    <CardTokenShell
      {...rest}
      label={label}
      icon={icon}
      labelGlyph={labelGlyph}
      labelMode={labelMode}
      labelLayout={labelLayout}
      layout={layout}
      labelRail={labelRail}
      pressable={canApply}
      // Own the press so it never bubbles to the card press (open issue),
      // then hand it to the wrapping trigger so the picker opens. Read-only
      // tokens wrap no trigger and own nothing — their press is the card's.
      onClick={
        canApply
          ? (e) => {
              e.stopPropagation();
              onClick?.(e);
            }
          : onClick
      }
      aria-label={canApply ? `Edit ${label.toLowerCase()}` : undefined}
    >
      {children}
    </CardTokenShell>
  );
}

export function IssueCardExtraProperty({
  propertyKey,
  issue,
  canApply,
  onStageChange,
  onProjectTypeChange,
  labelMode = "icon",
  labelLayout = "horizontal",
  layout = "wrap",
  labelRail,
}: {
  propertyKey: ExtraPropertyKey;
  issue: IssueRow;
  /** Viewer may edit (card edit access) — gates the selector popovers. */
  canApply: boolean;
  onStageChange: (stageId: string) => void;
  onProjectTypeChange: (issueTypeId: string) => void;
  /** "Field labels" display option — `text` spells the property name in the
   *  label segment, `icon` keeps the glyph (name in the card's tooltip). */
  labelMode?: FieldLabelMode;
  labelLayout?: TextLabelLayout;
  layout?: FieldLayout;
  labelRail?: number;
}) {
  const { stageMap, workflowMap, issueTypes } = useWorkspaceData();
  const tokenProps = { labelMode, labelLayout, layout, labelRail };

  if (propertyKey === "stage") {
    const stage = issue.stageId ? stageMap.get(issue.stageId) : undefined;
    if (!stage) return null;
    // Non-interactive EntityTag (no detail dialog) — the popover owns the
    // change; the token chrome carries the edit affordance.
    const chip = (
      <IssueCardPropertyToken
        {...tokenProps}
        label="Stage"
        icon={PROPERTY_ICON.stage}
        labelGlyph="value"
        canApply={canApply}
      >
        <EntityTag
          entity={{
            kind: "stage",
            id: stage.stageId,
            stage,
            workflowId: stage.workflowId ?? undefined,
          }}
          weight="regular"
          interactive={false}
        />
      </IssueCardPropertyToken>
    );
    if (!canApply) return chip;
    return (
      <SelectorPopover
        chip={chip}
        sheetTitle="Stage"
        content={(close) => (
          <StageSelector
            variant="inline"
            workflowId={issue.workflowId}
            value={issue.stageId}
            onValueChange={(stageId) => {
              onStageChange(stageId);
              close();
            }}
            onOpenChange={(next) => {
              if (!next) close();
            }}
          />
        )}
      />
    );
  }

  if (propertyKey === "projectType") {
    if (!issue.issueType) return null;
    /* Foreground label like the stage token — a muted label read as a
       non-interactive item next to its editable siblings (Spencer). */
    const chip = (
      <IssueCardPropertyToken
        {...tokenProps}
        label="Project Type"
        icon={PROPERTY_ICON.issueType}
        labelGlyph="value"
        canApply={canApply}
      >
        <EntityTag
          entity={{
            kind: "issueType",
            id: issue.issueType.issueTypeId,
            issueType: issue.issueType,
          }}
          weight="regular"
          interactive={false}
        />
      </IssueCardPropertyToken>
    );
    if (!canApply) return chip;
    return (
      <SelectorPopover
        chip={chip}
        sheetTitle="Project Type"
        content={(close) => (
          <IssueTypeSelector
            variant="inline"
            issueTypes={issueTypes}
            value={issue.issueTypeId ?? undefined}
            onValueChange={(issueTypeId) => {
              onProjectTypeChange(issueTypeId);
              close();
            }}
          />
        )}
      />
    );
  }

  if (propertyKey === "workflow") {
    const workflow = workflowMap.get(issue.workflowId);
    if (!workflow) return null;
    return (
      <IssueCardPropertyToken
        {...tokenProps}
        label="Workflow"
        icon={PROPERTY_ICON.workflow}
        labelGlyph="value"
        canApply={false}
      >
        <EntityTag
          entity={{
            kind: "workflow",
            id: workflow.workflowId,
            workflow,
          }}
          weight="regular"
          interactive={false}
        />
      </IssueCardPropertyToken>
    );
  }

  // Created / Updated — short date, read-only.
  const dateStr =
    propertyKey === "created" ? issue.creationDate : issue.lastUpdateDate;
  if (!dateStr) return null;
  return (
    <IssueCardPropertyToken
      {...tokenProps}
      label={propertyKey === "created" ? "Created" : "Updated"}
      icon={propertyKey === "created" ? "calendar" : "calendar-clock"}
      canApply={false}
    >
      <Box direction="column" shrink minWidth={0}>
        <DateText date={dateStr} format="short" size="sm" truncate={1} />
      </Box>
    </IssueCardPropertyToken>
  );
}
